import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Text, Tab, Separator } from '@cuemath/web-comps';

import COLORS from 'constants/colors';
import { capitalizeFirstLetter, determineMatchingTimezoneUS } from './utils';
import Slots from './slots';

const SlotContainer = ({
  slotsData,
  handleSetState,
  state,
  isEcnaLayoutV2,
  height,
  minWidth,
  padding,
  borderRadius,
  timeSlotRef,
  showFillingFast,
  timezone,
}) => {
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('morning');
  const { title, slots = [], slotsStyle, type } = slotsData || {};
  const selectedDate = slots?.filter(slot => slot.key === state[type])[0] || {};

  const {
    timeSlots: {
      subTitle,
      slots: timeSlots = [],
      type: timeType,
      slotsStyle: timeSlotsStyle,
    } = {},
  } = selectedDate;

  // Filter time slots by selected time of day
  const filteredTimeSlots = useMemo(() => {
    if (!timeSlots || !timeSlots.length) return [];

    return timeSlots.filter(slot => slot.timeOfDay === selectedTimeOfDay);
  }, [timeSlots, selectedTimeOfDay]);

  // Time of day tab options
  const timeOfDayTabs = useMemo(() => {
    const availableTimes = new Set();

    timeSlots.forEach(slot => {
      if (slot.timeOfDay) {
        availableTimes.add(slot.timeOfDay);
      }
    });

    return [
      { key: 'morning', label: 'Morning' },
      { key: 'afternoon', label: 'Afternoon' },
      { key: 'evening', label: 'Evening' },
    ].filter(tab => availableTimes.has(tab.key));
  }, [timeSlots]);

  // Set default tab to first available time of day
  useEffect(() => {
    if (
      timeOfDayTabs.length > 0 &&
      !timeOfDayTabs.find(tab => tab.key === selectedTimeOfDay)
    ) {
      setSelectedTimeOfDay(timeOfDayTabs[0].key);
    }
  }, [timeOfDayTabs, selectedTimeOfDay]);

  return (
    <div>
      <Text
        variant={isEcnaLayoutV2 ? 'eyebrow2' : 'body1Regular'}
        color={isEcnaLayoutV2 && COLORS.BLACK_75}
      >
        {capitalizeFirstLetter(title)}
      </Text>
      <Separator height={isEcnaLayoutV2 ? 12 : 16} />
      <Slots
        type={type}
        slots={slots}
        selectedTab={state[type]}
        slotsStyle={slotsStyle}
        handleSetState={handleSetState}
        height={height || '48px'}
        borderRadius={borderRadius}
        minWidth={minWidth}
        padding={padding}
        showFillingFast={showFillingFast}
      />
      <div ref={timeSlotRef} style={{ top: '-20px' }}>
        {subTitle && (
          <>
            <Text variant="body1Regular" color={COLORS.BLACK}>
              Select a time slot
            </Text>
            <Separator height={8} />
          </>
        )}

        {/* Time of day tabs */}
        {timeOfDayTabs.length > 1 && (
          <>
            <Tab
              data={timeOfDayTabs}
              selectedTab={selectedTimeOfDay}
              onClick={setSelectedTimeOfDay}
              variant="iconTab"
              height="40px"
              borderRadius="0px"
              minWidth="120px"
              padding="8px 16px"
              justifyContent="flex-start"
              mobileJustifyContent="flex-start"
              activeTextColor={COLORS.BLACK}
              marginRight="0px"
            />
          </>
        )}

        <Slots // time slots
          type={timeType}
          slots={filteredTimeSlots}
          selectedTab={state[timeType]?.from}
          titleVariant={isEcnaLayoutV2 && 'eyebrow2'}
          slotsStyle={timeSlotsStyle}
          handleSetState={handleSetState}
          showTimeSlots={!!filteredTimeSlots.length}
          textColor={COLORS.BLACK_75}
          separatorHeight={isEcnaLayoutV2 && 12}
          borderRadius="16px"
          minWidth="110px"
        />
      </div>
    </div>
  );
};

SlotContainer.propTypes = {
  slotsData: PropTypes.shape({}).isRequired,
  handleSetState: PropTypes.func.isRequired,
  state: PropTypes.shape({}).isRequired,
  isEcnaLayoutV2: PropTypes.bool,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  minWidth: PropTypes.string,
  padding: PropTypes.string,
  showFillingFast: PropTypes.bool,
  timeSlotRef: PropTypes.shape({}),
};

SlotContainer.defaultProps = {
  isEcnaLayoutV2: false,
  height: '',
  borderRadius: '',
  minWidth: '',
  padding: '',
  showFillingFast: false,
  timeSlotRef: '',
};

export default SlotContainer;
